import { queryHelpers, useMediaQuery } from '@leagueplatform/genesis-core';
import { FullScreenContainer } from 'components/full-screen-container/full-screen-container.component';
import { usePatientPortalContext } from 'contexts/patient-portal-provider.context';
import {
  CONTAINER_BACKGROUND_COLOR,
  PATIENT_PORTAL_SYSTEM_IDS,
} from 'types/patient-portal-system.types';
import { useGetUserProfile } from 'hooks/queries/use-get-user-profile.hook';
import { Stepper } from 'components/stepper/stepper.component';
import { ErrorState } from 'components/error-state/error-state.component';
import { ERROR_TYPES } from 'hooks/use-error-content/use-error-content.hook';
import { TermsStep } from './form-steps/terms/terms-step.component';
import { MarketingStep } from './form-steps/marketing/marketing-step.component';
import { MobileNumberStep } from './form-steps/mobile-number/mobile-number-step.component';
import { CodeVerificationStep } from './form-steps/code-verification/code-verification-step.component';
import { PersonalInformationStep } from './form-steps/personal-information/personal-information-step.component';
import { PersonalInfoReviewStep } from './form-steps/personal-information/personal-information-review-step.component';
import { CompleteStep } from './form-steps/complete/complete-step.component';
import {
  OnboardingErrorProvider,
  useOnboardingError,
} from './contexts/onboarding-error.context';
import { OnboardingFormProvider } from './contexts/onboarding-form.context';
import { getOnboardingStepFlags } from './onboarding-account-setup.selectors';

interface OnboardingAccountSetupProps {
  onOnboardingComplete: (error?: Error) => void;
}

const DUMMY_MOBILE_NUMBER = '+61412345678';

const OnboardingAccountSetupInner = ({
  onOnboardingComplete,
}: OnboardingAccountSetupProps) => {
  // --- TEMP ---
  // Workaround to disable some features to allow testing
  // --- Features/Changes ---
  // - Disabling mobile verification steps
  // - Adding dummy number for user phone number setup in personal information review step
  // - Reverting back to 'phoneNumber' field in user profile
  const disableMobileVerify =
    localStorage.getItem('DISABLE_MOBILE_VERIFY') === 'true';
  const { error, reportError } = useOnboardingError();
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));
  const { systemId } = usePatientPortalContext();
  const { data } = useGetUserProfile();
  const primaryMobileNumber = data?.phoneNumber;
  const mobileNumber = disableMobileVerify
    ? DUMMY_MOBILE_NUMBER
    : primaryMobileNumber;

  const isAmplar = systemId === PATIENT_PORTAL_SYSTEM_IDS.AMPLAR;
  const {
    shouldRenderTermsStep,
    shouldRenderMarketingStep,
    shouldRenderPhoneNumberStep,
  } = getOnboardingStepFlags(data, systemId);

  if (error) {
    return (
      <ErrorState
        isFullPage
        errorType={ERROR_TYPES.API_ERROR}
        error={error}
        buttonAction={() => {
          reportError(null);
          window.location.reload();
        }}
      />
    );
  }

  return (
    <OnboardingFormProvider primaryMobileNumber={mobileNumber}>
      <FullScreenContainer
        horizontalAlignment="center"
        css={{
          backgroundColor: isMobile
            ? 'transparent'
            : CONTAINER_BACKGROUND_COLOR[systemId],
        }}
      >
        <Stepper>
          {shouldRenderTermsStep && <TermsStep />}
          {shouldRenderMarketingStep && <MarketingStep />}
          {shouldRenderPhoneNumberStep &&
            !disableMobileVerify && [
              <MobileNumberStep key="mobile-number" />,
              <CodeVerificationStep key="code-verification" />,
            ]}
          {!isAmplar && [
            <PersonalInformationStep key="personal-information" />,
            <PersonalInfoReviewStep key="personal-information-review" />,
          ]}
          <CompleteStep onComplete={() => onOnboardingComplete()} />
        </Stepper>
      </FullScreenContainer>
    </OnboardingFormProvider>
  );
};

export const OnboardingAccountSetup = (props: OnboardingAccountSetupProps) => (
  <OnboardingErrorProvider>
    <OnboardingAccountSetupInner {...props} />
  </OnboardingErrorProvider>
);
